import { useContext, useMemo } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import {
  hasPermission,
  hasRole,
  canPerformAction,
  getAllUserPermissions,
  getPermissionsByModule,
  getAccessibleModules,
  getAuthorizedMenuItems,
  canAccessRoute,
  getModuleActions,
  isAdmin,
  isComptable,
  isGestionnaireRH,
  isGestionnairePedagogique,
  isSuperviseur,
  ROLES,
  MODULES,
  ACTIONS
} from '../utils/permissions';

/**
 * Hook personnalisé pour la gestion des permissions
 * @returns {Object} Objet avec toutes les fonctions de vérification des permissions
 */
export const usePermissions = () => {
  const { user } = useContext(AuthContext);

  // Mémoriser les calculs pour éviter les recalculs inutiles
  const permissions = useMemo(() => {
    if (!user) return [];
    return getAllUserPermissions(user);
  }, [user]);

  const accessibleModules = useMemo(() => {
    if (!user) return {};
    return getAccessibleModules(user);
  }, [user]);

  const menuItems = useMemo(() => {
    if (!user) return [];
    return getAuthorizedMenuItems(user);
  }, [user]);

  return {
    // Informations de base
    user,
    permissions,
    accessibleModules,
    menuItems,

    // Vérifications de permissions
    hasPermission: (permission) => hasPermission(user, permission),
    hasRole: (roleSlug) => hasRole(user, roleSlug),
    canPerformAction: (module, action) => canPerformAction(user, module, action),
    canAccessRoute: (path) => canAccessRoute(user, path),

    // Vérifications de rôles spécifiques
    isAdmin: () => isAdmin(user),
    isSuperviseur: () => isSuperviseur(user),
    isComptable: () => isComptable(user),
    isGestionnaireRH: () => isGestionnaireRH(user),
    isGestionnairePedagogique: () => isGestionnairePedagogique(user),

    // Utilitaires par module
    getPermissionsByModule: (module) => getPermissionsByModule(user, module),
    getModuleActions: (module) => getModuleActions(user, module),

    // Constantes
    ROLES,
    MODULES,
    ACTIONS,

    // Vérifications rapides pour les modules principaux
    payments: {
      canRead: hasPermission(user, 'payments.read'),
      canCreate: hasPermission(user, 'payments.create'),
      canUpdate: hasPermission(user, 'payments.update'),
      canDelete: hasPermission(user, 'payments.delete'),
      canExport: hasPermission(user, 'payments.export')
    },

    teachers: {
      canRead: hasPermission(user, 'teachers.read'),
      canCreate: hasPermission(user, 'teachers.create'),
      canUpdate: hasPermission(user, 'teachers.update'),
      canDelete: hasPermission(user, 'teachers.delete')
    },

    schedules: {
      canRead: hasPermission(user, 'schedules.read'),
      canCreate: hasPermission(user, 'schedules.create'),
      canUpdate: hasPermission(user, 'schedules.update'),
      canDelete: hasPermission(user, 'schedules.delete')
    },

    students: {
      canRead: hasPermission(user, 'students.read'),
      canCreate: hasPermission(user, 'students.create'),
      canUpdate: hasPermission(user, 'students.update'),
      canDelete: hasPermission(user, 'students.delete')
    },

    classes: {
      canRead: hasPermission(user, 'classes.read'),
      canCreate: hasPermission(user, 'classes.create'),
      canUpdate: hasPermission(user, 'classes.update'),
      canDelete: hasPermission(user, 'classes.delete')
    },

    grades: {
      canRead: hasPermission(user, 'grades.read'),
      canCreate: hasPermission(user, 'grades.create'),
      canUpdate: hasPermission(user, 'grades.update'),
      canDelete: hasPermission(user, 'grades.delete')
    },

    bulletins: {
      canRead: hasPermission(user, 'bulletins.read'),
      canCreate: hasPermission(user, 'bulletins.create'),
      canUpdate: hasPermission(user, 'bulletins.update'),
      canDelete: hasPermission(user, 'bulletins.delete'),
      canExport: hasPermission(user, 'bulletins.export'),
      canPrint: hasPermission(user, 'bulletins.print')
    },

    users: {
      canRead: hasPermission(user, 'users.read'),
      canCreate: hasPermission(user, 'users.create'),
      canUpdate: hasPermission(user, 'users.update'),
      canDelete: hasPermission(user, 'users.delete'),
      canManage: hasPermission(user, 'users.manage')
    },

    administration: {
      canManage: hasPermission(user, 'administration.manage')
    },

    reports: {
      canRead: hasPermission(user, 'reports.read'),
      canExport: hasPermission(user, 'reports.export')
    }
  };
};

/**
 * Hook pour vérifier une permission spécifique
 * @param {string} permission - Permission à vérifier
 * @returns {boolean}
 */
export const useHasPermission = (permission) => {
  const { user } = useContext(AuthContext);
  return useMemo(() => hasPermission(user, permission), [user, permission]);
};

/**
 * Hook pour vérifier un rôle spécifique
 * @param {string} roleSlug - Rôle à vérifier
 * @returns {boolean}
 */
export const useHasRole = (roleSlug) => {
  const { user } = useContext(AuthContext);
  return useMemo(() => hasRole(user, roleSlug), [user, roleSlug]);
};

/**
 * Hook pour vérifier si l'utilisateur peut effectuer une action sur un module
 * @param {string} module - Module
 * @param {string} action - Action
 * @returns {boolean}
 */
export const useCanPerformAction = (module, action) => {
  const { user } = useContext(AuthContext);
  return useMemo(() => canPerformAction(user, module, action), [user, module, action]);
};

/**
 * Hook pour obtenir les actions disponibles sur un module
 * @param {string} module - Module
 * @returns {Object}
 */
export const useModuleActions = (module) => {
  const { user } = useContext(AuthContext);
  return useMemo(() => getModuleActions(user, module), [user, module]);
};